import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/format";
import api from "../lib/api";

export default function CartCrossSell({ cartItems }) {
  const { t } = useTranslation();
  const { add } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!cartItems || cartItems.length === 0) {
      setProducts([]);
      return;
    }
    setLoading(true);
    api
      .post("/cart/recommendations", {
        product_ids: cartItems.map((i) => i.product_id),
        limit: 4,
      })
      .then((r) => {
        if (cancelled) return;
        setProducts(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Re-run when ids change (not qty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems.map((i) => i.product_id).join("|")]);

  if (!loading && products.length === 0) return null;

  return (
    <section
      className="mt-12 border-t border-white/10 pt-10"
      data-testid="cart-cross-sell"
    >
      <div className="flex items-center gap-2 mb-6">
        <Sparkles size={16} strokeWidth={1.5} className="text-[#FFD700]" />
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
          {t("cart.cross_sell_eyebrow", "Souvent achetés ensemble")}
        </div>
      </div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter text-white">
        {t("cart.cross_sell_title", "Les clients ont aussi acheté")}
      </h2>
      <p className="mt-2 text-sm text-white/55 max-w-xl">
        {t(
          "cart.cross_sell_subtitle",
          "Complétez votre routine en un clic — sélection basée sur les achats de clients comme vous."
        )}
      </p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {loading
          ? [0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-[#0A2A4F] border border-white/5 aspect-[3/4] animate-pulse"
                data-testid="cross-sell-skeleton"
              />
            ))
          : products.map((p) => (
              <CrossSellCard key={p.id} product={p} onAdd={() => add(p, 1)} />
            ))}
      </div>
    </section>
  );
}

function CrossSellCard({ product, onAdd }) {
  const lowStock = product.stock > 0 && product.stock <= 8;
  const out = product.stock === 0;
  return (
    <div
      className="group flex flex-col bg-[#0A2A4F] border border-white/10 hover:border-white/30 transition-colors"
      data-testid={`cross-sell-card-${product.id}`}
    >
      <Link
        to={`/product/${product.id}`}
        className="block relative aspect-square overflow-hidden bg-[#001F3F]"
      >
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {product.best_seller && !out && (
          <div className="absolute top-2 left-2 bg-[#FFD700] text-[#001F3F] text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5">
            ★ Best-Seller
          </div>
        )}
        {lowStock && (
          <div className="absolute top-2 right-2 bg-[#FF6B6B] text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5">
            −{product.stock}
          </div>
        )}
        {out && (
          <div className="absolute inset-0 bg-[#001F3F]/75 backdrop-blur-sm flex items-center justify-center">
            <span className="text-[10px] font-bold uppercase tracking-widest text-white border border-white/40 px-3 py-1">
              Rupture
            </span>
          </div>
        )}
      </Link>
      <div className="p-3 sm:p-4 flex-1 flex flex-col">
        <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">
          {product.category === "smart" ? "Smart Tech" : "Wellness"}
        </div>
        <Link
          to={`/product/${product.id}`}
          className="mt-1.5 text-sm font-medium text-white hover:underline underline-offset-4 line-clamp-2 min-h-[2.5rem]"
          data-testid={`cross-sell-name-${product.id}`}
        >
          {product.name}
        </Link>
        <div className="mt-auto pt-3 flex items-center justify-between gap-2">
          <div className="text-sm font-bold text-white whitespace-nowrap">
            {formatPrice(product.price)}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!out) onAdd();
            }}
            disabled={out}
            className="inline-flex items-center justify-center gap-1 bg-white text-[#001F3F] hover:bg-[#FFD700] transition-all uppercase tracking-[0.2em] text-[10px] font-bold px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`cross-sell-add-${product.id}`}
            aria-label={`Ajouter ${product.name}`}
          >
            <Plus size={12} strokeWidth={2.5} />
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}
